import { FinancialPlanAccounts, FinancialPlanData } from '../types/financialPlan';
import { applyRothIraCapToLumpSumSplit } from './lgcRothIra';

export const ROUTING_ACCOUNT_KEYS: (keyof FinancialPlanAccounts)[] = [
  'k401',
  'hsa',
  'rothIra',
  'taxable',
];

const SURPLUS_ROUTING_KEYS: (keyof FinancialPlanAccounts)[] = ['hsa', 'rothIra', 'taxable'];

function clampNonNeg(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function allocationMode(plan: FinancialPlanData): 'percent' | 'dollar' {
  return plan.accountSettings?.allocationMode === 'dollar' ? 'dollar' : 'percent';
}

function totalAccountContributions(accounts: FinancialPlanAccounts): number {
  return ROUTING_ACCOUNT_KEYS.reduce((s, k) => s + clampNonNeg(accounts[k].monthlyContribution), 0);
}

/** Per-account share of investable dollars (percent mode: pct/100; sums to total allocation, may be < 1). */
export function accountInvestmentWeights(
  plan: FinancialPlanData,
): Record<keyof FinancialPlanAccounts, number> {
  const weights = {} as Record<keyof FinancialPlanAccounts, number>;
  const mode = allocationMode(plan);

  if (mode === 'percent') {
    const totalPct = SURPLUS_ROUTING_KEYS.reduce(
      (s, k) => s + clampNonNeg(plan.accounts[k].contributionPct ?? 0),
      0,
    );
    if (totalPct > 0) {
      weights.k401 = 0;
      SURPLUS_ROUTING_KEYS.forEach((k) => {
        weights[k] = clampNonNeg(plan.accounts[k].contributionPct ?? 0) / 100;
      });
      return weights;
    }
  }

  const totalContrib = SURPLUS_ROUTING_KEYS.reduce(
    (s, k) => s + clampNonNeg(plan.accounts[k].monthlyContribution),
    0,
  );
  weights.k401 = 0;
  if (totalContrib <= 0) {
    const share = 1 / SURPLUS_ROUTING_KEYS.length;
    SURPLUS_ROUTING_KEYS.forEach((k) => {
      weights[k] = share;
    });
    return weights;
  }
  SURPLUS_ROUTING_KEYS.forEach((k) => {
    weights[k] = clampNonNeg(plan.accounts[k].monthlyContribution) / totalContrib;
  });
  return weights;
}

export function distributeExtraToAccounts(
  plan: FinancialPlanData,
  extraMonthly: number,
): Record<keyof FinancialPlanAccounts, number> {
  const perAccount = {} as Record<keyof FinancialPlanAccounts, number>;
  if (extraMonthly <= 0) {
    ROUTING_ACCOUNT_KEYS.forEach((k) => {
      perAccount[k] = 0;
    });
    return perAccount;
  }
  const weights = accountInvestmentWeights(plan);
  ROUTING_ACCOUNT_KEYS.forEach((k) => {
    perAccount[k] = extraMonthly * weights[k];
  });
  return perAccount;
}

export function lumpSumSplitByAccountWeight(
  plan: FinancialPlanData,
  lumpSum: number,
  options?: { age?: number; rothContributedAnnual?: number },
): Record<keyof FinancialPlanAccounts, number> {
  const split = {} as Record<keyof FinancialPlanAccounts, number>;
  ROUTING_ACCOUNT_KEYS.forEach((k) => {
    split[k] = 0;
  });
  if (lumpSum <= 0) return split;
  const weights = accountInvestmentWeights(plan);
  ROUTING_ACCOUNT_KEYS.forEach((k) => {
    split[k] = lumpSum * weights[k];
  });
  if (options?.age == null) return split;
  return applyRothIraCapToLumpSumSplit(split, options.age, options.rothContributedAnnual ?? 0);
}

export function addLumpSumToAccountsByWeight(
  balances: Record<keyof FinancialPlanAccounts, number>,
  plan: FinancialPlanData,
  lumpSum: number,
  options?: { age?: number; rothContributedAnnual?: number },
): Record<keyof FinancialPlanAccounts, number> {
  if (lumpSum <= 0) return balances;
  const next = { ...balances } as Record<keyof FinancialPlanAccounts, number>;
  const cappedSplit = lumpSumSplitByAccountWeight(plan, lumpSum, options);
  ROUTING_ACCOUNT_KEYS.forEach((k) => {
    next[k] += cappedSplit[k];
  });
  return next;
}
